import { useEffect, useState } from 'react';

interface JobDescriptionContentProps {
  description: string;
}

export const JobDescriptionContent = ({ description }: JobDescriptionContentProps) => {
  const [formattedContent, setFormattedContent] = useState('');

  useEffect(() => {
    const formatJobDescription = (text: string) => {
      // Clean CDATA markers
      let content = text.replace(/\[CDATA\[|\]\]/g, '').trim();
      
      // Filter out redundant job info lines before processing
      const redundantKeywords = [
        '40 Stunden pro Woche', '35 Stunden pro Woche', '30 Stunden pro Woche', 'Stunden pro Woche', 'Wochenstunden',
        'unbefristet', 'befristet', 'Festanstellung', 'Zeitvertrag',
        'Frühester Beginn ab sofort', 'Frühester Beginn', 'ab sofort', 'Beginn ab', 'Starttermin', 'Eintrittstermin',
        'Vollzeit', 'Teilzeit', 'Arbeitszeit', 'Stellenart', 'Vertrag', 'Anstellung',
        'Beschäftigungsart', 'Arbeitsverhältnis'
      ];
      
      // Filter out lines containing redundant keywords
      const lines = content.split('\n');
      const filteredLines = lines.filter(line => {
        const trimmedLine = line.trim();
        return !redundantKeywords.some(keyword => 
          trimmedLine.toLowerCase().includes(keyword.toLowerCase()) ||
          trimmedLine === keyword
        );
      });
      content = filteredLines.join('\n');
      
      // Split into sections based on common patterns
      const sections = content.split(/\n\s*\n/).filter(section => section.trim());
      
      let formattedHtml = '';
      
      sections.forEach((section, index) => {
        const lines = section.split('\n').map(line => line.trim()).filter(line => line);
        
        if (lines.length === 0) return;
        
        // Check if first line is a heading (common patterns)
        const firstLine = lines[0];
        const isHeading = 
          firstLine.endsWith(':') ||
          ['Das Unternehmen', 'Ihre Aufgaben', 'Das bringen Sie mit', 'Freuen Sie sich auf', 'Wir bieten', 'Anforderungen', 'Qualifikationen', 'Benefits', 'Was Sie erwartet', 'Ihre Qualifikationen', 'Unser Angebot'].some(heading => 
            firstLine.toLowerCase().includes(heading.toLowerCase())
          ) ||
          (firstLine.length < 50 && !firstLine.includes('•') && !firstLine.includes('-'));
        
        if (isHeading) {
          // This is a heading section
          formattedHtml += `<h3 class="text-xl font-display font-semibold text-primary mb-3 mt-6">${firstLine.replace(':', '')}</h3>`;
          
          // Process remaining lines
          if (lines.length > 1) {
            const contentLines = lines.slice(1);
            formattedHtml += formatContentLines(contentLines);
          }
        } else {
          // Regular content
          formattedHtml += formatContentLines(lines);
        }
      });
      
      return formattedHtml;
    };
    
    const formatContentLines = (lines: string[]) => {
      let html = '';
      let inList = false;
      
      lines.forEach(line => {
        if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
          // Check if the bullet point content should be filtered out
          const bulletContent = line.replace(/^[•\-*]\s*/, '');
          const redundantKeywords = [
            '40 Stunden pro Woche', '35 Stunden pro Woche', '30 Stunden pro Woche', 'Stunden pro Woche', 'Wochenstunden',
            'unbefristet', 'befristet', 'Festanstellung', 'Zeitvertrag',
            'Frühester Beginn ab sofort', 'Frühester Beginn', 'ab sofort', 'Beginn ab', 'Starttermin', 'Eintrittstermin',
            'Vollzeit', 'Teilzeit', 'Arbeitszeit', 'Stellenart', 'Vertrag', 'Anstellung',
            'Beschäftigungsart', 'Arbeitsverhältnis'
          ];
          
          // Skip if bullet content contains redundant keywords or is empty
          if (bulletContent.trim() === '' || 
              redundantKeywords.some(keyword => 
                bulletContent.toLowerCase().includes(keyword.toLowerCase())
              )) {
            return; // Skip this bullet point entirely
          }
          
          if (!inList) {
            html += '<ul class="list-none space-y-2 my-4">';
            inList = true;
          }
          html += `<li class="flex items-start gap-3">
            <span class="inline-flex items-center justify-center w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
            <span>${bulletContent}</span>
          </li>`;
        } else {
          if (inList) {
            html += '</ul>';
            inList = false;
          }
          
          // Check if it's a standalone text line
          if (line.length > 0) {
            // Skip lines that contain key job info already displayed in badges
            const redundantKeywords = [
              'Stunden pro Woche', '40 Stunden', '35 Stunden', '30 Stunden', 'Wochenstunden',
              'unbefristet', 'befristet', 'Festanstellung', 'Zeitvertrag',
              'Frühester Beginn', 'ab sofort', 'Beginn ab', 'Starttermin', 'Eintrittstermin',
              'Vollzeit', 'Teilzeit', 'Arbeitszeit', 'Stellenart', 'Vertrag', 'Anstellung',
              'Beschäftigungsart', 'Arbeitsverhältnis'
            ];
            
            if (redundantKeywords.some(keyword => line.toLowerCase().includes(keyword.toLowerCase()))) {
              return; // Skip this line as it's already shown in badges
            }
            
            // Check if it looks like a subheading (short line, no punctuation at end)
            if (line.length < 80 && !line.endsWith('.') && !line.includes('•')) {
              html += `<p class="font-medium text-foreground my-3">${line}</p>`;
            } else {
              html += `<p class="mb-3 leading-relaxed">${line}</p>`;
            }
          }
        }
      });
      
      if (inList) {
        html += '</ul>';
      }
      
      return html;
    };
    
    setFormattedContent(formatJobDescription(description));
  }, [description]);

  return (
    <div 
      className="job-description-content"
      dangerouslySetInnerHTML={{ __html: formattedContent }}
    />
  );
};